import { Dialog, Switch, Transition } from "@headlessui/react";
import { Icon } from "@iconify/react";
import React, { Fragment, useEffect, useState } from "react";
import { toast } from "react-toastify";
import UpdateHooks from "../../../Hooks/UpdateHooks";
import AdminModalForm from "./AdminModalForm";

const AdminDoctorDetailsModal = ({ isOpen, closeModal, doctor, doctorId }) => {
  const [account, setAccount] = useState(doctor?.isActive);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  useEffect(() => {
    if (doctor) {
      setName(doctor.name || "");
      setEmail(doctor.email || "");
      setPhone(doctor.phone || "");
      setAccount(doctor.isActive);
    }
  }, [doctor]);

  const handelUpdateAccount = async () => {
    await UpdateHooks(`https://servercareseebd.careseebd.com/api/v1/user/${doctorId}`, {
      isActive: !account,
    });
    toast.success("Account status updated!");
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={closeModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-2xl transition-all border border-slate-100">
                {/* Header panel */}
                <div className="flex justify-between items-center px-6 py-4 border-b border-slate-100">
                  <Dialog.Title as="h3" className="text-sm font-extrabold text-slate-800 uppercase tracking-wider flex items-center gap-2">
                    <Icon icon="solar:user-speak-bold-duotone" className="text-secondary text-lg" />
                    <span>Doctor Settings & License Control</span>
                  </Dialog.Title>
                  <button
                    onClick={closeModal}
                    className="p-1.5 border border-slate-200 hover:border-red-200 text-slate-400 hover:text-red-500 rounded-lg transition duration-150 cursor-pointer"
                  >
                    <Icon icon="solar:close-square-linear" className="text-lg" />
                  </button>
                </div>

                {/* Modal Body */}
                <div className="p-6 space-y-6">
                  {/* Doctor Info Section */}
                  <div className="bg-slate-50/50 p-4 rounded-xl border border-slate-100 space-y-4">
                    <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wide px-0.5">Doctor Profile Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Name View Card */}
                      <div className="flex items-center gap-3 bg-white p-3 rounded-xl border border-slate-200/60 shadow-xs">
                        <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-xl">
                          <Icon icon="solar:user-linear" className="text-lg" />
                        </div>
                        <div>
                          <span className="block text-[10px] font-bold text-slate-450 uppercase tracking-wide">Name</span>
                          <span className="block text-xs font-bold text-slate-700 select-all mt-0.5">{name}</span>
                        </div>
                      </div>

                      {/* Email View Card */}
                      <div className="flex items-center gap-3 bg-white p-3 rounded-xl border border-slate-200/60 shadow-xs">
                        <div className="p-2.5 bg-sky-50 text-sky-600 rounded-xl">
                          <Icon icon="solar:letter-linear" className="text-lg" />
                        </div>
                        <div className="overflow-hidden">
                          <span className="block text-[10px] font-bold text-slate-455 uppercase tracking-wide">Email</span>
                          <span className="block text-xs font-bold text-slate-700 select-all truncate mt-0.5" title={email}>{email}</span>
                        </div>
                      </div>

                      {/* Phone View Card */}
                      <div className="flex items-center gap-3 bg-white p-3 rounded-xl border border-slate-200/60 shadow-xs">
                        <div className="p-2.5 bg-amber-50 text-amber-600 rounded-xl">
                          <Icon icon="solar:phone-linear" className="text-lg" />
                        </div>
                        <div>
                          <span className="block text-[10px] font-bold text-slate-450 uppercase tracking-wide">Phone Number</span>
                          <span className="block text-xs font-bold text-slate-700 select-all mt-0.5">{phone || "Not specified"}</span>
                        </div>
                      </div>

                      {/* Active Status toggle switch Card */}
                      <div className="flex items-center justify-between bg-white p-3 rounded-xl border border-slate-200/60 shadow-xs">
                        <div className="flex items-center gap-3">
                          <div className={`p-2.5 rounded-xl ${account ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-500"}`}>
                            <Icon icon="solar:shield-keyhole-linear" className="text-lg" />
                          </div>
                          <div>
                            <span className="block text-[10px] font-bold text-slate-450 uppercase tracking-wide">Access State</span>
                            <span className={`block text-xs font-extrabold mt-0.5 ${account ? "text-emerald-650" : "text-rose-500"}`}>
                              {account ? "Active Account" : "Inactive / Blocked"}
                            </span>
                          </div>
                        </div>
                        <Switch
                          checked={account}
                          onChange={(checked) => {
                            setAccount(checked);
                            handelUpdateAccount();
                          }}
                          className={`${account ? "bg-secondary" : "bg-slate-200"
                            } relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500/20`}
                        >
                          <span
                            aria-hidden="true"
                            className={`${account ? "translate-x-5" : "translate-x-0"
                              } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out`}
                          />
                        </Switch>
                      </div>
                    </div>
                  </div>

                  {/* Form section containing license/pricing parameters */}
                  <div className="space-y-3">
                    <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wide px-1">Subscription Plans & Inventory Prices</h4>
                    <AdminModalForm doctor={doctor} id={doctorId} />
                  </div>
                </div>

                <div className="bg-slate-50 px-6 py-3 flex justify-end rounded-b-2xl border-t border-slate-100">
                  <button
                    onClick={closeModal}
                    className="bg-white hover:bg-slate-50 text-slate-600 border border-slate-200 font-bold py-1.5 px-4 rounded-xl text-xs transition cursor-pointer"
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default AdminDoctorDetailsModal;
